"use client";

import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

import { cancelChurnFlow } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/flow";
import { applyChurnDiscountAction } from "@/data/payment/actions/applyChurnDiscountAction";
import { availableChurnDiscountAction } from "@/data/payment/actions/availableChurnDiscountAction";
import {
  type CancellationReason,
  type CancellationStep,
  type OfferedDiscount,
} from "@/types/types";

export const useSubscriptionCancellation = ({
  closeModal,
  isOpen,
}: {
  closeModal: () => void;
  isOpen: boolean;
}) => {
  const [currentStep, setCurrentStep] = useState<CancellationStep>(
    cancelChurnFlow[0]
  );
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [stepHistory, setStepHistory] = useState<CancellationStep[]>([
    cancelChurnFlow[0],
  ]);
  const [offeredDiscount, setOfferedDiscount] =
    useState<OfferedDiscount | null>(null);
  const [wasCancelledImmediately, setWasCancelledImmediately] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(cancelChurnFlow[0]);
      setSelectedReason(null);
      setStepHistory([cancelChurnFlow[0]]);
      setOfferedDiscount(null);
      setWasCancelledImmediately(false);
    }
  }, [isOpen]);

  const router = useRouter();
  const posthog = usePostHog();

  const handleClose = () => {
    closeModal();
    // Every close re-syncs the account page — several paths mutate server state before closing (discount applied, subscription cancelled, stale subscription detected).
    router.refresh();
  };

  const handleBack = () => {
    if (stepHistory.length > 1) {
      const newHistory = [...stepHistory];
      newHistory.pop(); // Remove current step
      const previousStep = newHistory[newHistory.length - 1];
      setStepHistory(newHistory);
      setCurrentStep(previousStep);
      if (previousStep.id === "ask_cancellation_reason") {
        setSelectedReason(null);
      }
    }
  };

  const handleChoice = async (
    nextStepId: string,
    selectedReason_: CancellationReason,
    feedbackText_: string | null
  ) => {
    // Store feedback when provided
    if (feedbackText_ !== null) {
      setFeedbackText(feedbackText_);
    }
    // Store reason when provided
    if (selectedReason_ !== null) {
      setSelectedReason(selectedReason_);
    }

    // Special case for apply_discount
    if (nextStepId === "apply_discount") {
      const availableOfferedDiscount = await availableChurnDiscountAction();
      if (availableOfferedDiscount === null) {
        const nextStep = cancelChurnFlow.find(
          (step: CancellationStep) => step.id === "show_feedback_form"
        );
        if (nextStep !== undefined) {
          setCurrentStep(nextStep);
          setStepHistory([...stepHistory, nextStep]);
        }
        return;
      } else {
        setOfferedDiscount(availableOfferedDiscount);
      }
    }

    // When user stays, apply discount if available
    if (
      nextStepId === "user_stays" &&
      currentStep.id === "apply_discount" &&
      // Should never happen, just for TypeScript
      offeredDiscount !== null
    ) {
      try {
        const result = await applyChurnDiscountAction();
        if (!result.success) {
          console.error("Failed to apply discount:", result.error);
          return;
        }
      } catch (error) {
        console.error("Error applying discount:", error);
        return;
      }
    }

    // Tracking
    if (
      nextStepId === "user_stays" ||
      nextStepId === "confirm_cancellation" ||
      nextStepId === "confirm_contact"
    ) {
      const isRetained = nextStepId === "user_stays";
      const timestamp = new Date().toISOString();

      let feedbackText__: string | null = null;
      if (feedbackText_ !== null) {
        feedbackText__ = feedbackText_;
      } else if (feedbackText !== null) {
        feedbackText__ = feedbackText;
      }

      posthog.people.set({
        last_cancellation_attempt: timestamp,
        last_cancellation_outcome: isRetained ? "retained" : "churned",
        last_cancellation_reason: selectedReason,
        last_cancellation_feedback: feedbackText__,
        last_cancellation_flow: stepHistory.map((step) => step.id).join(" -> "),
        ...(isRetained &&
          currentStep.id === "apply_discount" && {
            last_accepted_discount:
              offeredDiscount !== null ? offeredDiscount.terms : false,
            last_discount_acceptance_date: timestamp,
          }),
      });

      posthog.capture("subscriptionCancellationFlow", {
        outcome: isRetained ? "retained" : "churned",
        reason: selectedReason,
        feedback: feedbackText__,
        wasOfferedDiscount: offeredDiscount !== null,
        discountTerms: offeredDiscount !== null ? offeredDiscount.terms : null,
        acceptedDiscount: isRetained && currentStep.id === "apply_discount",
        flow: stepHistory.map((step) => step.id).join(" -> "),
      });
    }

    // When user wants support, redirect to contact page
    if (nextStepId === "confirm_contact") {
      router.push("/contact");
      return;
    }

    const nextStep = cancelChurnFlow.find(
      (step: CancellationStep) => step.id === nextStepId
    );

    if (nextStep !== undefined) {
      setCurrentStep(nextStep);
      setStepHistory([...stepHistory, nextStep]);
    }
  };

  const handleCancellationCompleted = async (cancelledImmediately: boolean) => {
    // Remember which cancellation branch ran so the success step can tell an overdue user that no further payment will be collected.
    setWasCancelledImmediately(cancelledImmediately);
    await handleChoice("success_cancellation", null, null);
  };

  // Fisher-Yates shuffle algorithm for choices
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  return {
    currentStep,
    selectedReason,
    stepHistory,
    offeredDiscount,
    wasCancelledImmediately,
    handleChoice,
    handleBack,
    handleClose,
    handleCancellationCompleted,
    shuffleArray,
  };
};
